/**
 * @file reservations.api.js
 * @module ReservationsAPI
 * @description HTTP API wrapper for reservation-related endpoints (create, read, update, cancel, export, etc.).
 * All methods return an Axios Promise created by axiosInstance(token).
 */

import { axiosInstance } from "./axios";

export const ReservationsAPI = {

    /**
     * Create a new reservation.
     * @param {Object} reservation - Reservation payload.
     * @param {string} [token] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created reservation.
     */
    createReservation: (payload, token = "") => {
        return axiosInstance(token).post(`/reservations`, payload)
    },

    /**
     * Retrieve reservations with pagination and optional query.
     * @param {number} [limit=20] - Page size.
     * @param {number} [page=1] - Page number.
     * @param {string} [query=""] - Optional query string for filtering.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to reservations list.
     */
    getReservations: (limit = 20, page = 1, query = "", token = "") => {
        return axiosInstance(token).get(`/reservations?limit=${limit}&page=${page}${query ? `&${query}` : ""}`)
    },

    /**
    * Retrieves metadata for reservations filtering with optional query parameters
    * @param {string} [query=""] - Query string for filtering metadata
    * @param {string} token - Authentication token
    * @returns {Promise<ApiResponse>} Promise that resolves to reservation metadata
    */
    getMetadataReservations: (query = "", token) => {
        return axiosInstance(token).get(`/reservations/status/counts${query ? `?${query}` : ""}`)
    },

    /**
     * Get a single reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {boolean} [populate=true] - Whether to populate referenced fields.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to reservation data.
     */
    getReservation: (reservationId, populate = true, token = "") => {
        return axiosInstance(token).get(`/reservations/${reservationId}?populate=${populate}`)
    },

    /**
     * Update a reservation by id.
     * @param {string} id - Reservation identifier.
     * @param {Object} payload - Updated reservation data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated reservation.
     */
    updateReservation: (id, payload, token = "") => {
        return axiosInstance(token).put(`/reservations/${id}`, payload)
    },

    /**
     * Delete a reservation by id.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion.
     */
    deleteReservation: (reservationId, token = "") => {
        return axiosInstance(token).delete(`/reservations/${reservationId}`)
    },

    /**
     * Delete multiple reservations.
     * @param {Array} [reservations=[]] - Array of reservation ids to delete.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise indicating deletion result.
     */
    deleteManyReservationsByIds: (payload = [], token = "") => {
        return axiosInstance(token).post(`/reservations/bulk-delete`, payload)
    },

    /**
     * Confirm a reservation by id.
     * @param {string} id - Reservation identifier.
     * @param {Object} payload - Confirmation payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to confirmation result.
     */
    confirmReservationById: (id, payload, token = "") => {
        return axiosInstance(token).post(`/reservations/${id}/confirm`, payload);
    },

    /**
     * Cancel a reservation by id with reason and refund eligibility.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} reason - Cancellation reason.
     * @param {boolean} refundEligibility - Whether refund applies.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to cancellation result.
     */
    canlcelReservationById: (reservationId, reason, token = "") => {
        return axiosInstance(token).post(`/reservations/${reservationId}/cancel`, { reason: reason })
    },

    /**
     * Send a payment link to customers.
     * @param {Object} payload - Payment link payload.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to the send result.
     */
    sendPaymentLink: (payload, token = "") => {
        return axiosInstance(token).post(`/payment-link`, payload)
    },

    /**
     * Get histories for a reservation (paginated).
     * @param {string} reservationId - Reservation identifier.
     * @param {number} limit - Page size.
     * @param {number} page - Page number.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to histories list.
     */
    getHistoriesByReservationId: (reservationId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/reservations/${reservationId}/histories?limit=${limit}&page=${page}`)
    },

    /**
     * Update a history entry by id.
     * @param {string} historyId - History entry id.
     * @param {Object} history - Updated history data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },

    /**
     * Resend reservation-related emails to a list of recipients.
     * @param {string} reservationId - Reservation identifier.
     * @param {Array} recipients - Array of recipient emails or identifiers.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    resendEmailToUsersByReservationId(reservationId, recipients, token = "") {
        return axiosInstance(token).post(`/reservations/${reservationId}/resend-email`, { recipients: recipients })
    },

    /**
     * Change reservation status (any status) with reason.
     * @param {string} reservationId - Reservation identifier.
     * @param {string} status - New status.
     * @param {string} reason - Reason for status change.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to updated reservation.
     */
    changeRservationStatus: (reservationId, status, reason, token = "") => {
        return axiosInstance(token).patch(`/reservations/${reservationId}/updateAnyStatus`, { status: status, reason: reason })
    },

    /**
     * Get reservations metrics. Optional query parameter support (if present in implementation).
     * @param {string} [query]
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to metrics data.
     */
    getReservationsMetrics: (query, token = "") => {
        return axiosInstance(token).get(`/reservations-metrics${query ? `?${query}` : ""}`)
    },

    /**
     * Create a partial driver profile for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {Object} driver - Driver data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to created driver.
     */
    createPartialDriverProfile: (branchId, driver, token = "") => {
        return axiosInstance(token).post(`/branches/${branchId}/drivers`, driver);
    },

    /**
     * Get drivers options for a branch.
     * @param {string} branchId - Branch identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to drivers options.
     */
    getDriversByBranchId: (branchId, token = "") => {
        return axiosInstance(token).get(`/branches/${branchId}/drivers-options`);
    },

    /**
     * Send notifications to passengers associated with a reservation.
     * @param {string} reservationId - Reservation identifier.
     * @param {Array} passengers - Array of passenger objects/ids to notify.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendNotificationByReservationId: (reservationId, passengers, token = "") => {
        return axiosInstance(token).post(`/reservations/${reservationId}/passengers-notifications`, { passengers: passengers })
    },

    /**
     * Download PDF (quote, voucher or invoice) for a reservation or invoice id.
     * Returns blob responseType for file download.
     * @param {string} pdfType - 'quote'|'voucher'|'invoice'
     * @param {string} identifire - Reservation or invoice identifier.
     * @param {Object} client - Client object with type and _id.
     * @param {string} voucherType - Voucher type (if applicable).
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to binary blob.
     */
    downloadPDF: (pdfType, identifire, client, voucherType, token = '') => {
        if (["quote", "voucher"].includes(pdfType)) {
            return axiosInstance(token).get(`/reservations/${identifire}/generate-pdf?pdfType=${pdfType}&userType=${client.type}&userReference=${client._id}&voucherType=${voucherType}`, {
                responseType: 'blob',
            });
        } else {
            return axiosInstance(token).get(`/invoices/${identifire}/generate-pdf?pdfType=invoice&userType=${client.type}&userReference=${client._id}&voucherType=${voucherType}`, {
                responseType: 'blob',
            });
        }
    },

    /**
     * Download ticket PDFs for a reservation.
     * @param {string} identifire - Reservation identifier.
     * @param {Object} client - Client object.
     * @param {number} quantity - Number of tickets.
     * @param {string} ticketReference - Ticket reference id.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to blob file.
     */
    downloadPDFTickets: (identifire, client, quantity, ticketReference, token = '') => {
        return axiosInstance(token).get(`/tickets/${identifire}/generate-pdf?pdfType=tickets&userType=${client.type}&userReference=${client._id}&quantity=${quantity}&ticketReference=${ticketReference}`, {
            responseType: 'blob',
        });
    },

    /**
   * Creates a new booking for a product through OTA partner integration
   * @param {Object} payload - Booking data to be sent in the request body
   * @param {string} [token=""] - Authentication token for the request
   * @returns {Promise<ApiResponse>} Promise that resolves to the API response with booking data
   */
    createReservationForExperience: (payload, token = "") => {
        return axiosInstance(token).post(`/reservations/experiences`, payload)
    },

    /**
     * Updates an existing booking for a product through OTA partner integration
     * @param {string} id - The ID of the reservation to update
     * @param {Object} payload - Updated booking data to be sent in the request body
     * @param {string} [token=""] - Authentication token for the request
     * @returns {Promise<ApiResponse>} Promise that resolves to the API response with updated booking data
     */
    updateReservationForExperience: (id, payload, token = "") => {
        return axiosInstance(token).patch(`/reservations/${id}/experiences`, payload)
    },

    /**
     * Confirm OTA reservation by id.
     * @param {string} reservationId
     * @param {Object} payload
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise}
     */
    confirmExperienceReservationById: (id, payload, token = '') => {
        return axiosInstance(token).post(`/reservations/experiences/${id}/confirm`, payload)
    },

    /**
     * Cancel OTA reservation by id.
     * @param {string} id identifier of the reservation to cancel
     * @param {Object} payload - cancellation details to be sent in the request body
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} - Axios promise resolving to the API response with cancellation result
     */
    cancelExperienceReservationById: (id, payload, token = '') => {
        return axiosInstance(token).post(`/reservations/experiences/${id}/cancel`, payload)
    },

    /**
     * Get commission by product reference.
     * @param {string} productId - Product identifier.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to commission details.
     */
    getCommissionByReference: (productId, token = "") => {
        return axiosInstance(token).get(`/commissions/reference/${productId}`);
    },

    /**
     * Create shareable reservation link.
     * @param {Object} payload - Payload containing reservation details.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to share link data.
     */
    createShareReservationsLinks: (payload, token = "") => {
        return axiosInstance(token).post(`/shared-links`, payload);
    },

    /**
     * Create shareable reservation list.
     * @param {Object} payload - Payload containing list details.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to shared list data.
     */
    createShareReservationsList: (payload, token = "") => {
        return axiosInstance(token).post(`/shared-list`, payload);
    },
    /**
     * Create multiple transfer reservations from a CSV file upload.
     * @param {File} file - CSV file containing reservations data.
     * @param {string} [token=""] - Optional authentication token.
     * @returns {Promise} Axios promise resolving to bulk creation result.
     */
    bulkCreateTransferReservations: (file, token = "") => {
        const formData = new FormData();
        formData.append("file", file);
        return axiosInstance(token).post(`/bulk-create-transfer-reservations`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
    },

    /**
     * Retrieves pending change information for a specific reservation by its ID
     * @param {string} id - Unique identifier of the reservation to check for pending changes
     * @param {string} token - Authentication token
     * @returns {Promise<ApiResponse>} Promise that resolves to pending change data if exists
     */
    getPendingChangeByReservationId: (id, token) => {
        return axiosInstance(token).get(`/reservations/${id}/pending-change`)
    },
    /**
     * Cancels a reservation's pending change while it's still awaiting payment,
     * releasing its pending payment (Stripe Checkout Session expired)
     * @param {string} id - Unique identifier of the reservation
     * @param {string} reason - Reason for cancelling the change
     * @param {string} token - Authentication token
     * @returns {Promise<ApiResponse>} Promise that resolves to the cancelled change
     */
    cancelPendingChangeByReservationId: (id, reason, token) => {
        return axiosInstance(token).post(`/reservations/${id}/pending-change/cancel`, { reason })
    },
}
